import { JobStatusType, JobType, type JobPriorityType } from '@/enum'
import type {
  IDocument,
  IJobAssociatedAssetInfo,
  IJobAssociatedAssetUpdate,
  IJobAssociatedMaintenanceIssue,
  IJobAssociatedMaintenancePart,
  IJobAssociatedMaintenanceType,
  IJobAssociatedTaskSheet,
  IJobAssociatedTaskSheetUpdate,
  IJobForm,
  IJobScheduleOptions,
  IVendor
} from '@/interfaces'
import type { IAppUser } from '@/interfaces/app-user/IAppUser'
import type { INextTriggerInfo } from '@/interfaces/jobs/INextTriggerInfo'
import type { IValueBasedTrigger } from '@/interfaces/jobs/IValueBasedTrigger'
import { DEFAULT_JOB_PRIORITY } from '@/utils/constants'
import { toLocalDateTimeFromUtc } from '@/utils/helpers'
import AppUser from '../AppUser'
import Base from '../Base'
import DocumentModel from '../DocumentModel'
import JobAssociatedTaskSheetUpdate from './JobAssociatedTaskSheetUpdate'
import JobScheduleOptions from './JobScheduleOptions'
import NextTriggerInfo from './NextTriggerInfo'
import ValueBasedTrigger from './ValueBasedTrigger'
import JobAssociatedTaskSheet from './JobAssociatedTaskSheet'
import type CalendarEvent from '../CalendarEvent'

class JobForm extends Base implements IJobForm {
  title: string
  description?: string
  type?: JobType
  status?: JobStatusType
  priority?: JobPriorityType
  odometerTrigger?: IValueBasedTrigger
  machineHoursTrigger?: IValueBasedTrigger
  asset?: IJobAssociatedAssetUpdate
  assetInfo?: IJobAssociatedAssetInfo
  assignedEmployeeIds?: string[]
  assignedEmployees?: IAppUser[] | null
  issueReporterId?: string
  issueReporter?: IAppUser
  maintenanceTypes?: IJobAssociatedMaintenanceType[]
  maintenanceParts?: IJobAssociatedMaintenancePart[]
  maintenanceIssues?: IJobAssociatedMaintenanceIssue[]
  workOrderVendor?: IVendor
  workOrderUrl?: string
  taskSheets: IJobAssociatedTaskSheet[]
  documents?: IDocument[]
  notes?: string
  isEmployeeSafetyChecksCompleted?: boolean
  scheduleId?: string
  schedule?: IJobScheduleOptions
  scheduledAt?: string | null
  dueAt?: string | null
  completedAt?: string | null
  startedAt?: string | null
  number?: string | null

  // form based fields
  jobStartDate?: string | null
  jobDueDate?: string | null

  // selections
  isOdometerBasedSelected?: boolean
  isMachineHoursBasedSelected?: boolean
  isTimeBasedSelected?: boolean
	triggerInfo?: INextTriggerInfo

  constructor(jobForm?: any) {
    super(jobForm)

    this.title = jobForm?.title ?? ''
    this.number = jobForm?.number ?? null
    this.description = jobForm?.description
    this.type = jobForm?.type
    this.status = jobForm?.status
    this.priority = jobForm?.priority || DEFAULT_JOB_PRIORITY
    this.odometerTrigger = new ValueBasedTrigger(jobForm?.odometerTrigger)
    this.machineHoursTrigger = new ValueBasedTrigger(jobForm?.machineHoursTrigger)
    this.scheduledAt = jobForm?.scheduledAt
    this.dueAt = jobForm?.dueAt
    this.asset = jobForm?.asset
    this.assetInfo = jobForm?.assetInfo
    this.assignedEmployeeIds = jobForm?.assignedEmployees
      ? jobForm.assignedEmployees.map((assignedEmployee: any) => assignedEmployee.employeeId)
      : []
    this.assignedEmployees = jobForm?.assignedEmployees
      ? jobForm?.assignedEmployees.map(
          (assignedEmployee: any) => { 
            return {
              ...new AppUser(assignedEmployee.employee),
              order: assignedEmployee.order
            } 
          }
        )
      : null
    this.workOrderVendor = jobForm?.workOrderVendor
    this.workOrderUrl = jobForm?.workOrderUrl
    this.taskSheets =
      jobForm?.taskSheets?.map((taskSheet: any) => new JobAssociatedTaskSheet(taskSheet)) ?? []
    this.documents = jobForm?.documents?.map((document: any) => new DocumentModel(document)) ?? []
    this.notes = jobForm?.notes
    this.isEmployeeSafetyChecksCompleted = !!jobForm?.isEmployeeSafetyChecksCompleted
    this.scheduleId = jobForm?.scheduleId
    this.schedule = new JobScheduleOptions(jobForm?.schedule?.options)
    this.completedAt = jobForm?.completedAt ? toLocalDateTimeFromUtc(jobForm?.completedAt) : null
    this.scheduledAt = jobForm?.scheduledAt ? toLocalDateTimeFromUtc(jobForm?.scheduledAt) : null
    this.startedAt = jobForm?.startedAt ? toLocalDateTimeFromUtc(jobForm?.startedAt) : null
		this.triggerInfo = new NextTriggerInfo(jobForm?.triggerInfo)

    // form based fields
    this.jobStartDate = this.scheduledAt
    this.jobDueDate = jobForm?.dueAt ? toLocalDateTimeFromUtc(jobForm?.dueAt) : null

    if (this.odometerTrigger?.value) {
      this.isOdometerBasedSelected = true
    }

    if (this.machineHoursTrigger?.value) {
      this.isMachineHoursBasedSelected = true
    }

    if (this.jobStartDate) {
      this.isTimeBasedSelected = true
    }
  }
}

export default JobForm
